import React from 'react'
import { AutocompleteChangeReason, AutocompleteInputChangeReason } from '@mui/material'
import { ComboBox } from 'components/common'
import { useCompanies } from 'contexts'
import { ORPHAN_PARENT_COMPANY } from 'contexts/companies/constants'
import { Company } from 'models'
import { entityDisplayName } from 'utils/helpers'
import { theme } from 'utils/theme'

const NavBarSellerFamily = () => {
  const {
    selectedParent,
    parentCompanies,
    isLoading,
    isParentsFetching,
    setSelectedParent,
    setParentSearchValue,
    setParentSearchFilterValue,
    parentSearchValue,
  } = useCompanies()

  const parentOptions = React.useMemo(
    () => [...parentCompanies, ORPHAN_PARENT_COMPANY],
    [parentCompanies],
  )

  const handleParentChange = React.useCallback(
    (
      _event: React.SyntheticEvent<Element, Event>,
      option: Company | null,
      reason: AutocompleteChangeReason,
    ) => {
      if (reason === 'clear') {
        setParentSearchValue('', { skipCommit: true })
        setParentSearchFilterValue('')
        return
      }
      const nextLabel = option ? entityDisplayName(option, true) : ''
      setSelectedParent(option as Company)
      setParentSearchValue(nextLabel, { skipCommit: true })
      setParentSearchFilterValue('')
    },
    [setParentSearchValue, setParentSearchFilterValue, setSelectedParent],
  )

  const handleParentInputChange = React.useCallback(
    (
      _event: React.SyntheticEvent<Element, Event>,
      value: string,
      reason: AutocompleteInputChangeReason,
    ) => {
      if (reason === 'input') {
        setParentSearchValue(value)
        return
      }

      if (reason === 'clear') {
        setParentSearchValue('', { skipCommit: true })
        setParentSearchFilterValue('')
      }
    },
    [setParentSearchValue, setParentSearchFilterValue],
  )

  const highlightBoxStyle = React.useMemo(
    () => ({
      '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
      },
    }),
    [],
  )

  return (
    <ComboBox
      id="seller-parent"
      options={parentOptions}
      getOptionLabel={(option: Company) => entityDisplayName(option, true)}
      getOptionKey={(option: Company) => option.id}
      selectedOption={selectedParent}
      onSelection={handleParentChange}
      onInputChange={handleParentInputChange}
      disabled={isLoading}
      loading={isParentsFetching}
      inputValue={parentSearchValue}
      boxStyle={selectedParent ? undefined : highlightBoxStyle}
    />
  )
}

export default NavBarSellerFamily
